/** Native modal events preserve browser dialog semantics and clean up on unmount. */
export function bindPreviewDialog(dialog: HTMLDialogElement, first: HTMLButtonElement | null, last: HTMLButtonElement | null, onClose: () => void) {
  const cancel = (event: Event) => { event.preventDefault(); onClose() }
  const keydown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return
    if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus() }
  }
  const click = (event: MouseEvent) => {
    if (event.target !== dialog) return
    const rect = dialog.getBoundingClientRect()
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose()
  }
  dialog.addEventListener('cancel', cancel)
  dialog.addEventListener('keydown', keydown)
  dialog.addEventListener('click', click)
  return () => {
    dialog.removeEventListener('cancel', cancel)
    dialog.removeEventListener('keydown', keydown)
    dialog.removeEventListener('click', click)
  }
}

/** Observe every loaded document, replacing the old observer on navigation. */
export function bindPreviewFrame(frame: HTMLIFrameElement, onHeight: (height: number) => void, onClose: () => void) {
  let observer: ResizeObserver | undefined
  let document: Document | null = null
  // Keyboard events inside a frame do not bubble to the containing dialog.
  const keydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault()
      onClose()
    }
  }
  const load = () => {
    observer?.disconnect()
    document?.removeEventListener('keydown', keydown)
    document = frame.contentDocument
    const body = document?.body
    if (!document || !body) return
    document.addEventListener('keydown', keydown)
    const update = () => onHeight(Math.ceil(body.getBoundingClientRect().height))
    update()
    observer = new ResizeObserver(update)
    observer.observe(body)
  }
  frame.addEventListener('load', load)
  if (frame.contentDocument?.readyState === 'complete') load()
  return () => {
    frame.removeEventListener('load', load)
    document?.removeEventListener('keydown', keydown)
    observer?.disconnect()
  }
}
